import { createPortal } from "react-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParentZIndex } from "components/designV2/stacking/stackingZIndexContext";
import { ZIndex } from "config/zIndex";
import { useOnClickOutside } from "hooks/useOnClickOutside";
import { Container, Label, Message } from "./timePicker.styles";
import {
  TimeParts,
  TimePickerProps,
  TimePickerSize,
  TimePickerState,
} from "./timePicker.types";
import {
  formatTime,
  parseFormattedTime,
  parseTimeInput,
} from "./timePicker.utils";
import { TimePickerInput } from "./timePickerInput";
import { TimePickerMenu } from "./timePickerMenu";
import { useTimePickerMenuPosition } from "./useTimePickerMenuPosition";

const DEFAULT_PLACEHOLDER = "12:00";

/** Time picker with free typing and hour/minute lists. */
export const TimePicker = (props: TimePickerProps): JSX.Element => {
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [draftValue, setDraftValue] = useState(props.value ?? "");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const size: TimePickerSize = props.size ?? "default";
  const openOnFocus = props.openOnFocus ?? true;
  const parentZIndex = useParentZIndex();
  const menuZIndex =
    props.menuZIndex ??
    (parentZIndex != null ? parentZIndex + 1 : ZIndex.dropdown);
  const menuPosition = useTimePickerMenuPosition({
    floatingRef: menuRef,
    isOpen,
    size,
    triggerRef,
  });

  useEffect(() => {
    if (!isFocused) {
      setDraftValue(props.value ?? "");
    }
  }, [isFocused, props.value]);

  useEffect(() => {
    if (!props.disabled) return;
    setIsOpen(false);
    setIsFocused(false);
  }, [props.disabled]);

  useOnClickOutside(
    [containerRef, menuRef],
    () => {
      setIsOpen(false);
      setIsFocused(false);
    },
    { enabled: isOpen || isFocused, eventType: "mousedown" },
  );

  const resolvedValue = isFocused ? draftValue : (props.value ?? "");
  const selectedTime = useMemo(
    () => parseTimeInput(resolvedValue),
    [resolvedValue],
  );
  const fallbackTime = useMemo(
    () => parseFormattedTime(props.value),
    [props.value],
  );

  const currentTime: TimeParts | null = selectedTime.ok
    ? selectedTime.value
    : fallbackTime.ok
      ? fallbackTime.value
      : null;

  const isFilled = Boolean(resolvedValue.length > 0);
  const resolvedErrorText =
    props.errorText ?? t("DesignComponents.DefaultError");

  const visualState = useMemo<TimePickerState>(() => {
    if (props.disabled) return "disabled";
    if (props.hasError) return "error";
    if (isFocused || isOpen) return "active";
    if (isFilled) return "filled";
    return "inactive";
  }, [props.disabled, props.hasError, isFocused, isOpen, isFilled]);

  const handleFocus = (): void => {
    if (props.disabled) return;
    setIsFocused(true);
    if (openOnFocus) setIsOpen(true);
    props.onFocus?.();
  };

  const handleBlur = (): void => {
    setIsFocused(false);
    setIsOpen(false);
    if (props.disabled) return;
    const parsed = parseTimeInput(draftValue);
    if (draftValue.trim().length === 0) {
      props.onChange?.("");
      props.onBlur?.();
      return;
    }
    if (parsed.ok) {
      const formatted = formatTime(parsed.value);
      setDraftValue(formatted);
      props.onChange?.(formatted);
    } else {
      setDraftValue(props.value ?? "");
    }
    props.onBlur?.();
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const nextValue = event.target.value;
    setDraftValue(nextValue);
    if (nextValue.trim().length === 0) {
      props.onChange?.("");
      return;
    }
    const parsed = parseTimeInput(nextValue);
    if (parsed.ok) {
      props.onChange?.(formatTime(parsed.value));
    }
  };

  const handleToggle = (): void => {
    if (props.disabled) return;
    setIsOpen((prev) => {
      const next = !prev;
      if (next && document.activeElement !== inputRef.current) {
        inputRef.current?.focus();
      }
      return next;
    });
  };

  const handleSelectHour = (hours: number): void => {
    if (props.disabled) return;
    const minutes = currentTime?.minutes ?? 0;
    const formatted = formatTime({ hours, minutes });
    setDraftValue(formatted);
    props.onChange?.(formatted);
  };

  const handleSelectMinute = (minutes: number): void => {
    if (props.disabled) return;
    const hours = currentTime?.hours ?? 0;
    const formatted = formatTime({ hours, minutes });
    setDraftValue(formatted);
    props.onChange?.(formatted);
    setIsOpen(false);
    props.onCommit?.(formatted);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (event.key === "Escape") {
      setIsOpen(false);
      props.onBlur?.();
    }
  };

  const menu = (
    <TimePickerMenu
      menuRef={menuRef}
      isOpen={isOpen}
      size={size}
      zIndex={menuZIndex}
      position={menuPosition}
      currentTime={currentTime}
      onSelectHour={handleSelectHour}
      onSelectMinute={handleSelectMinute}
    />
  );

  return (
    <Container
      ref={containerRef}
      aria-disabled={props.disabled}
      $fullWidth={props.fullWidth ?? false}
      $size={size}
    >
      {props.label && <Label>{props.label}</Label>}
      <TimePickerInput
        value={resolvedValue}
        placeholder={props.placeholder ?? DEFAULT_PLACEHOLDER}
        ariaLabel={props.ariaLabel}
        disabled={props.disabled}
        visualState={visualState}
        size={size}
        isOpen={isOpen}
        inputRef={inputRef}
        triggerRef={triggerRef}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onToggle={handleToggle}
      />
      {isOpen && typeof document !== "undefined"
        ? createPortal(menu, document.body)
        : null}
      {(visualState === "error" ? resolvedErrorText : props.hintText) && (
        <Message $state={visualState}>
          {visualState === "error" ? resolvedErrorText : props.hintText}
        </Message>
      )}
    </Container>
  );
};
